/**
 * useWarRoom — hook do War Room de campanha
 *
 * Combina duas fontes:
 *  - Stats (total/sent/delivered/read/failed): polling a cada 5 s (preciso)
 *  - Live feed: Supabase Realtime Postgres Changes em campaign_messages
 *  - Campaign status: Realtime em campaigns → invalida query para refetch
 *  - Throughput: janela deslizante de 60 s contada via eventos Realtime
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/* ── Tipos públicos ──────────────────────────────────────────────────────────── */

export interface WarRoomStats {
  total:     number;
  queued:    number;
  sent:      number;
  delivered: number;
  read:      number;
  failed:    number;
}

export interface LiveEvent {
  /** id único para React key */
  key:          string;
  contact_name: string | null;
  phone:        string;
  status:       'sent' | 'delivered' | 'read' | 'failed';
  error_title:  string | null;
  ts:           Date;
}

export interface WarRoomState {
  stats:       WarRoomStats;
  events:      LiveEvent[];
  isConnected: boolean;
  /** Mensagens processadas no último minuto (Realtime events) */
  throughput:  number;
  isLoading:   boolean;
}

const EMPTY_STATS: WarRoomStats = {
  total: 0, queued: 0, sent: 0, delivered: 0, read: 0, failed: 0,
};

const FEED_STATUSES = new Set(['sent', 'delivered', 'read', 'failed']);

/* ── Hook ────────────────────────────────────────────────────────────────────── */

/**
 * @param campaignId  UUID da campanha
 * @param isActive    true enquanto running|paused — controla polling e Realtime
 */
export function useWarRoom(
  campaignId: string | undefined,
  isActive   = true,
): WarRoomState {
  const qc = useQueryClient();

  const [events,      setEvents]      = useState<LiveEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [throughput,  setThroughput]  = useState(0);

  /** Timestamps dos eventos Realtime recentes (janela 60 s) */
  const recentTs = useRef<number[]>([]);

  /* ── 1. Stats via polling (5 s) ─────────────────────────────────────────── */

  const { data: stats = EMPTY_STATS, isLoading } = useQuery<WarRoomStats>({
    queryKey: ['war-room-stats', campaignId],
    queryFn: async (): Promise<WarRoomStats> => {
      if (!campaignId) return EMPTY_STATS;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('campaign_messages')
        .select('status')
        .eq('campaign_id', campaignId);

      if (error) throw error;

      const rows = (data ?? []) as { status: string }[];
      return {
        total:     rows.length,
        queued:    rows.filter((r) => r.status === 'queued').length,
        sent:      rows.filter((r) => r.status === 'sent').length,
        delivered: rows.filter((r) => r.status === 'delivered').length,
        read:      rows.filter((r) => r.status === 'read').length,
        failed:    rows.filter((r) => r.status === 'failed').length,
      };
    },
    enabled:         !!campaignId,
    refetchInterval: isActive ? 5_000 : false,
    staleTime:       0,
  });

  /* ── 2. Feed inicial (últimos 30 eventos) ───────────────────────────────── */

  useEffect(() => {
    if (!campaignId) return;

    void (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await (supabase as any)
        .from('campaign_messages')
        .select('id, phone, status, error_title, sent_at, contact:contacts(full_name)')
        .eq('campaign_id', campaignId)
        .in('status', ['sent', 'delivered', 'read', 'failed'])
        .order('updated_at', { ascending: false })
        .limit(30);

      if (!data) return;

      type Row = {
        id: string;
        phone: string;
        status: string;
        error_title: string | null;
        sent_at: string | null;
        contact: { full_name: string } | null;
      };

      setEvents(
        (data as Row[]).map((r) => ({
          key:          `init-${r.id}`,
          contact_name: r.contact?.full_name ?? null,
          phone:        r.phone,
          status:       r.status as LiveEvent['status'],
          error_title:  r.error_title,
          ts:           r.sent_at ? new Date(r.sent_at) : new Date(),
        })),
      );
    })();
  }, [campaignId]);

  /* ── 3. Realtime: feed de mensagens ─────────────────────────────────────── */

  const addEvent = useCallback((row: {
    id: string;
    phone: string;
    status: string;
    error_title?: string | null;
  }) => {
    if (!FEED_STATUSES.has(row.status)) return;

    const now = Date.now();
    recentTs.current = [...recentTs.current, now].filter((t) => now - t < 60_000);
    setThroughput(recentTs.current.length);

    const ev: LiveEvent = {
      key:          `rt-${row.id}-${row.status}-${now}`,
      contact_name: null,
      phone:        row.phone,
      status:       row.status as LiveEvent['status'],
      error_title:  row.error_title ?? null,
      ts:           new Date(),
    };

    setEvents((prev) => [ev, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    if (!campaignId || !isActive) return;

    /* canal: campaign_messages */
    const msgChannel = supabase
      .channel(`war-room-msg:${campaignId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'campaign_messages',
          filter: `campaign_id=eq.${campaignId}`,
        },
        (payload) => {
          const row = payload.new as {
            id: string;
            phone: string;
            status: string;
            error_title?: string | null;
          } | null;
          if (row?.status) addEvent(row);
          // Também invalida stats para refetch mais rápido
          qc.invalidateQueries({ queryKey: ['war-room-stats', campaignId] });
        },
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
      });

    /* canal: campaign status changes */
    const campChannel = supabase
      .channel(`war-room-camp:${campaignId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'campaigns',
          filter: `id=eq.${campaignId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
          qc.invalidateQueries({ queryKey: ['campaigns'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(msgChannel);
      void supabase.removeChannel(campChannel);
    };
  }, [campaignId, addEvent, qc]);

  /* ── 4. Decay do throughput (janela deslizante) ─────────────────────────── */

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      recentTs.current = recentTs.current.filter((t) => now - t < 60_000);
      setThroughput(recentTs.current.length);
    }, 5_000);
    return () => clearInterval(id);
  }, []);

  return { stats, events, isConnected, throughput, isLoading };
}
